import React, { useState, useEffect } from 'react';
import { useQuiz } from '../contexts/QuizContext';
import { quizService } from '../services/quizService';
// import { Button } from '../components/Button';
import { format } from 'date-fns';
import { supabase } from '../lib/supabase';
import { DeleteConfirmationModal } from '../components/DeleteConfirmationModal';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { HeaderElements } from '../components/HeaderElements';
import { AnimatedBackground } from '../components/AnimatedBackground';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { ArrowLeft, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface QuizBackup {
  id: string;
  quiz_id: string;
  quiz_data: any;
  backup_type: string;
  created_at: string;
  restored_at: string | null;
  metadata: {
    question_count: number;
    version: string;
    source: string;
    quiz_title: string;
  };
}

export const QuizBackupsPage: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { quizzes, loadQuizzes } = useQuiz();
  const navigate = useNavigate();
  const [backups, setBackups] = useState<Record<string, QuizBackup[]>>({});
  // Pre-delete snapshots of modules that no longer exist — the recovery shelf.
  const [deletedBackups, setDeletedBackups] = useState<QuizBackup[]>([]);
  const [showDeletedModal, setShowDeletedModal] = useState(false);
  // In-app confirm for both restore flavours (user request, 21 Jul: no
  // browser window.confirm dialogs).
  const [confirmRestore, setConfirmRestore] = useState<{ backup: QuizBackup; fromDeleted: boolean } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [restoring, setRestoring] = useState<string | null>(null);
  const [expandedQuiz, setExpandedQuiz] = useState<string | null>(null);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    backupId: string | null;
    isDeleting: boolean;
    // Set when deleting from the Deleted Modules shelf: purge EVERY
    // pre_delete snapshot of this quiz, not just the row shown — a
    // delete→restore→delete cycle leaves older snapshots that would
    // otherwise resurface on the shelf after "Delete Permanently".
    purgeQuizId?: string | null;
    backupInfo?: {
      created_at: string;
      backup_type: string;
      question_count: number;
      quiz_title: string;
    };
  }>({
    isOpen: false,
    backupId: null,
    isDeleting: false
  });


  useEffect(() => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
    loadBackups();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizzes]);

  const loadBackups = async () => {
    setLoading(true);
    setError(null);
    try {
      const backupsByQuiz: Record<string, QuizBackup[]> = {};

      // Load backups for each quiz
      for (const quiz of quizzes) {
        const quizBackups = await quizService.backupService.listBackups(quiz.id);
        if (quizBackups.length > 0) {
          backupsByQuiz[quiz.id] = quizBackups;
        }
      }

      setBackups(backupsByQuiz);
      setDeletedBackups(await quizService.backupService.listDeletedModuleBackups());
    } catch (err) {
      console.error('Error loading backups:', err);
      setError('Failed to load backups. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBackup = async (quizId: string) => {
    try {
      await quizService.backupService.createBackup(quizId, 'manual');
      await loadBackups(); // Refresh the list
    } catch (err) {
      console.error('Error creating backup:', err);
      setError('Failed to create backup. Please try again.');
    }
  };

  // Shared by both restore flavours. For deleted modules the upsert-by-
  // original-id recreates the quiz + questions with their old uuids, so audio
  // URLs and the module's backup history reconnect on their own.
  const performRestore = async (backupId: string) => {
    setRestoring(backupId);
    try {
      await quizService.backupService.restoreFromBackup(backupId);
      await loadQuizzes(); // Refresh quizzes after restore
      await loadBackups(); // Refresh backups list
    } catch (err) {
      console.error('Error restoring backup:', err);
      setError('Failed to restore backup. Please try again.');
    } finally {
      setRestoring(null);
    }
  };

  const handleDeleteClick = (backup: QuizBackup, quizTitle: string, fromDeletedShelf = false) => {
    setDeleteModal({
      isOpen: true,
      backupId: backup.id,
      isDeleting: false,
      purgeQuizId: fromDeletedShelf ? backup.quiz_id : null,
      backupInfo: {
        created_at: backup.created_at,
        backup_type: backup.backup_type,
        question_count: backup.metadata.question_count,
        quiz_title: quizTitle
      }
    });
  };

  const handleDeleteBackup = async () => {
    if (!deleteModal.backupId) return;

    setDeleteModal(prev => ({ ...prev, isDeleting: true }));
    try {
      if (deleteModal.purgeQuizId) {
        // Deleted Modules shelf: forget the module entirely — every
        // pre_delete snapshot it has, not just the newest.
        const { error } = await supabase
          .from('quiz_backups')
          .delete()
          .eq('quiz_id', deleteModal.purgeQuizId)
          .eq('backup_type', 'pre_delete');

        if (error) {
          console.error('Database deletion error:', error);
          throw error;
        }

        setDeletedBackups(prev => prev.filter(b => b.quiz_id !== deleteModal.purgeQuizId));
      } else {
        // Delete the backup from the database
        const { error } = await supabase
          .from('quiz_backups')
          .delete()
          .eq('id', deleteModal.backupId);

        if (error) {
          console.error('Database deletion error:', error);
          throw error;
        }
      }

      // Update local state to remove the deleted backup
      setBackups(prevBackups => {
        const newBackups = { ...prevBackups };
        Object.keys(newBackups).forEach(quizId => {
          newBackups[quizId] = newBackups[quizId].filter(backup => backup.id !== deleteModal.backupId);
          if (newBackups[quizId].length === 0) {
            delete newBackups[quizId];
          }
        });
        return newBackups;
      });
      setDeletedBackups(prev => prev.filter(b => b.id !== deleteModal.backupId));

      setDeleteModal({
        isOpen: false,
        backupId: null,
        isDeleting: false
      });
    } catch (err) {
      console.error('Error deleting backup:', err);
      setError('Failed to delete backup. Please try again.');
      await loadBackups();
    }
  };

  if (loading) {
    return (
      <div className="dashboard-bg">
        <AnimatedBackground />
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
          <div className="rounded-xl border border-gray-100 bg-white shadow-sm p-6 text-center">
            <div className="animate-pulse text-navy">Loading backups...</div>
          </div>
        </div>
      </div>
    );
  }

  const content = (
      <div className={embedded ? '' : "max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10"}>
        {!embedded && (
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-xl font-bold text-navy">Module Backups</h1>
            <button
              onClick={() => navigate('/admin')}
              className="bg-gray-200 hover:bg-gray-300 text-gray-900 border border-gray-300 rounded-lg px-3 py-1.5 transition-all duration-200 flex items-center gap-1.5 text-sm"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to Admin Panel
            </button>
          </div>
        )}
        
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 mb-6">
            {error}
          </div>
        )}

        {/* Deleted Modules lives behind a button + modal (user request, 21 Jul:
            an always-open panel would swallow the page once deletions pile up). */}
        <div className="flex justify-start mb-3">
          <button
            onClick={() => setShowDeletedModal(true)}
            className="bg-amber-100 hover:bg-amber-200 text-amber-900 border border-amber-200 rounded-lg px-3 py-1.5 text-sm font-medium transition-all duration-200"
          >
            Deleted Modules ({deletedBackups.length})
          </button>
        </div>

        {/* z-[60] to clear the admin header (z-50); the permanent-delete
            confirm (DeleteConfirmationModal, z-[70]) still stacks on top. */}
        {showDeletedModal && (
          <div className="fixed inset-0 z-[60] overflow-y-auto">
            <div className="flex min-h-screen items-center justify-center p-4">
              <div className="fixed inset-0 bg-black/50" onClick={() => setShowDeletedModal(false)} />
              <div className="relative bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[80vh] overflow-y-auto">
                <div className="sticky top-0 bg-amber-50 border-b border-amber-200 px-5 py-4 flex items-start justify-between rounded-t-xl">
                  <div>
                    <h2 className="text-base font-semibold text-amber-900">Deleted Modules</h2>
                    <p className="text-xs text-amber-800/80 mt-0.5">
                      Modules deleted from Manage Modules can be restored from here, complete with their questions and media.
                    </p>
                  </div>
                  <button
                    onClick={() => setShowDeletedModal(false)}
                    className="text-amber-900/60 hover:text-amber-900 p-1"
                  >
                    <X size={18} />
                  </button>
                </div>
                <div className="p-4">
                  {deletedBackups.length > 0 ? (
                    <div className="overflow-x-auto border border-gray-200 rounded">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-2.5 py-1.5 text-left font-medium text-gray-900">Module</th>
                            <th className="px-2.5 py-1.5 text-left font-medium text-gray-900">Deleted</th>
                            <th className="px-2.5 py-1.5 text-left font-medium text-gray-900">#Q</th>
                            <th className="px-2.5 py-1.5 text-left font-medium text-gray-900">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                          {deletedBackups.map(backup => (
                            <tr key={backup.id} className="hover:bg-gray-50">
                              <td className="px-2.5 py-1.5 font-medium text-gray-900">
                                {backup.metadata?.quiz_title || 'Unknown module'}
                              </td>
                              <td className="px-2.5 py-1.5 text-gray-700">
                                {format(new Date(backup.created_at), 'dd/MM/yyyy HH:mm')}
                              </td>
                              <td className="px-2.5 py-1.5 text-gray-700">
                                {backup.metadata?.question_count ?? 'N/A'}
                              </td>
                              <td className="px-2.5 py-1.5">
                                <div className="flex gap-1">
                                  <button
                                    onClick={() => setConfirmRestore({ backup, fromDeleted: true })}
                                    disabled={restoring === backup.id}
                                    className={`${
                                      restoring === backup.id ? 'bg-navy/50' : 'bg-navy hover:bg-navy/90'
                                    } text-white text-sm px-2 py-1 rounded transition-all duration-200 disabled:opacity-50`}
                                  >
                                    {restoring === backup.id ? 'Restoring…' : 'Restore'}
                                  </button>
                                  <button
                                    onClick={() => handleDeleteClick(backup, backup.metadata?.quiz_title || 'Unknown module', true)}
                                    className="bg-red-600 hover:bg-red-700 text-white text-sm px-2 py-1 rounded transition-all duration-200"
                                  >
                                    Delete Permanently
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-gray-500 italic text-sm">No deleted modules.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-2">
          {quizzes.map(quiz => (
            <div key={quiz.id} className="rounded-lg border border-gray-100 bg-white shadow-sm">
              <div
                className="flex justify-between items-center p-3 cursor-pointer hover:bg-gray-50 rounded"
                onClick={() => setExpandedQuiz(expandedQuiz === quiz.id ? null : quiz.id)}
              >
                <div className="flex items-center gap-2">
                  <ChevronDownIcon 
                    className={`w-5 h-5 transition-transform duration-200 text-gray-700 ${
                      expandedQuiz === quiz.id ? 'transform rotate-180' : ''
                    }`}
                  />
                  <h2 className="text-base font-semibold text-gray-900">{quiz.title}</h2>
                  <span className="text-sm text-gray-600">
                    ({backups[quiz.id]?.length || 0} backups)
                  </span>
                </div>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCreateBackup(quiz.id);
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-2 rounded-lg transition-all duration-200"
                >
                  Create Manual Backup
                </button>
              </div>

              <div className={`overflow-hidden transition-[max-height] duration-200 ease-in-out ${
                expandedQuiz === quiz.id ? 'max-h-[400px]' : 'max-h-0'
              }`}>
                <div className="px-4 pb-4">
                  {backups[quiz.id]?.length > 0 ? (
                    <div className="overflow-x-auto border border-gray-300 rounded">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-2.5 py-1.5 text-left font-medium text-gray-900">Created</th>
                            <th className="px-2.5 py-1.5 text-left font-medium text-gray-900">Type</th>
                            <th className="px-2.5 py-1.5 text-left font-medium text-gray-900">#Q</th>
                            <th className="px-2.5 py-1.5 text-left font-medium text-gray-900">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                          {backups[quiz.id].map(backup => (
                            <tr key={backup.id} className="border-b border-gray-200 hover:bg-gray-50">
                              <td className="px-2.5 py-1.5 text-gray-900">
                                {format(new Date(backup.created_at), 'MM/dd HH:mm')}
                              </td>
                              <td className="px-2.5 py-1.5 text-gray-900">
                                <span className="capitalize">{backup.backup_type}</span>
                              </td>
                              <td className="px-2.5 py-1.5 text-gray-900">
                                {backup.metadata?.question_count || 'N/A'}
                              </td>
                              <td className="px-2.5 py-1.5">
                                <div className="flex gap-1">
                                  <button
                                    onClick={() => setConfirmRestore({ backup, fromDeleted: false })}
                                    disabled={restoring === backup.id}
                                    className={`${
                                      restoring === backup.id
                                        ? 'bg-navy/50'
                                        : 'bg-navy hover:bg-navy/90'
                                    } text-white text-sm px-2 py-1 rounded transition-all duration-200 disabled:opacity-50`}
                                    title={backup.restored_at ? `Last restored: ${format(new Date(backup.restored_at), 'PPp')}` : 'Never restored'}
                                  >
                                    {restoring === backup.id ? '...' : 'Restore'}
                                  </button>
                                  <button
                                    onClick={() => handleDeleteClick(backup, quiz.title)}
                                    className="bg-red-600 hover:bg-red-700 text-white text-sm px-2 py-1 rounded transition-all duration-200"
                                  >
                                    Del
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-gray-600 italic text-sm">No backups available for this module.</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <DeleteConfirmationModal
          isOpen={deleteModal.isOpen}
          onClose={() => setDeleteModal({ isOpen: false, backupId: null, isDeleting: false })}
          onConfirm={handleDeleteBackup}
          isDeleting={deleteModal.isDeleting}
          backupInfo={deleteModal.backupInfo}
        />

        <ConfirmDialog
          open={confirmRestore !== null}
          onOpenChange={(open) => { if (!open) setConfirmRestore(null); }}
          title={confirmRestore?.fromDeleted ? 'Restore deleted module' : 'Restore backup'}
          description={
            confirmRestore
              ? confirmRestore.fromDeleted
                ? `Restore "${confirmRestore.backup.metadata?.quiz_title || 'this module'}"? It will reappear in Manage Modules with all its questions.`
                : `Restore the ${format(new Date(confirmRestore.backup.created_at), 'dd/MM/yyyy HH:mm')} backup of "${confirmRestore.backup.metadata?.quiz_title || 'this module'}"? This will overwrite the module's current content.`
              : ''
          }
          confirmLabel="Restore"
          onConfirm={() => {
            if (confirmRestore) {
              performRestore(confirmRestore.backup.id);
            }
            setConfirmRestore(null);
          }}
        />

        {quizzes.length === 0 && (
          <p className="text-gray-600 text-center text-sm">No modules found.</p>
        )}
      </div>
  );

  if (embedded) return content;

  return (
    <div className="dashboard-bg">
      <AnimatedBackground />
      <HeaderElements />
      {content}
    </div>
  );
};